import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { faBusinessTime } from '@fortawesome/pro-regular-svg-icons/faBusinessTime';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Space,
  Spin,
  type TableColumnsType,
  theme,
  Typography,
} from 'antd';
import InfiniteLoadingSelect from 'pos-client/components/generic/InfiniteLoadingSelect/InfiniteLoadingSelect.tsx';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { useFilters } from 'pos-client/hooks/useFilters.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { PosPageContent } from 'pos-client/layouts/page/PosPageContent.tsx';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';
import type {
  NonTradingDatesModel,
  OpeningHoursListFilterModel,
  TradingTimesModel,
} from 'pos-shared/models/openingHoursGroupSchema.ts';

import { LocationHoursModal } from './LocationHoursModal.tsx';

export const locationDays = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const mapLocationHours = (group: TradingTimesModel[]) =>
  locationDays.map((day) => {
    const matchedDay = group.find((tradingDay) => tradingDay.dayOfWeek === day);

    return (
      matchedDay ?? {
        closing: '--',
        opening: '--',
        id: day,
        dayOfWeek: day,
      }
    );
  });

export const LocationHours = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const canEditOpeningHours = hasPermissions('add.edit.location.openingHours');
  const { location } = useOutletContext<{
    location: LocationModel;
  }>();
  const { formFilters, onPageChange, handleFiltersChange } =
    useFilters<OpeningHoursListFilterModel>({
      pageNumber: 1,
      resultsPerPage: 250,
      sortAscending: true,
    });

  const { showSuccess, showError } = useNotifications();

  const trpcContext = trpc.useUtils();

  const openingGroupId =
    location.openingHoursGroupId !== null &&
    location.openingHoursGroupId !== undefined;

  const { data: openingHoursGroup, isLoading: isLoadingGroup } =
    trpc.getOpeningHoursGroupById.useQuery(
      {
        id: location.openingHoursGroupId as string,
      },
      {
        enabled: openingGroupId,
        select: (groups) => {
          return groups.results.slice().shift();
        },
      }
    );

  const { data: openingHoursGroups, isLoading: isLoadingAllGroups } =
    trpc.getAllOpeningHoursGroups.useQuery(formFilters);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedOpeningHoursGroupId, setSelectedOpeningHoursGroupId] =
    useState<string>('');

  const showModal = () => setIsModalOpen(true);
  const hideModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (!isLoadingAllGroups && location.openingHoursGroupId) {
      setSelectedOpeningHoursGroupId(location.openingHoursGroupId);
    }
  }, [isLoadingAllGroups, location.openingHoursGroupId]);

  const { mutate: assignOpeningHoursGroup, isLoading: isUpdatingGroup } =
    trpc.locations.updateLocationOpeningHoursGroup.useMutation({
      onSuccess: () => {
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'opening hours group',
          })
        );
      },
      onError: () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'opening hours group',
          })
        );
      },
    });

  const handleOpeningHoursGroupAssign = () => {
    if (selectedOpeningHoursGroupId) {
      assignOpeningHoursGroup(
        {
          ...location,
          openingHoursGroupId: selectedOpeningHoursGroupId,
        },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            trpcContext.locations.findLocations.invalidate();
          },
          onError: () => {
            setIsModalOpen(false);
          },
        }
      );
    }
  };

  const openingHoursColumns: TableColumnsType<TradingTimesModel> = [
    {
      title: t('common.time.unit.day'),
      key: 'dayOfWeek',
      dataIndex: 'dayOfWeek',
    },
    {
      title: t('locations.trading.openingTime'),
      key: 'opening',
      dataIndex: 'opening',
    },
    {
      title: t('locations.trading.closingTime'),
      key: 'closing',
      dataIndex: 'closing',
    },
  ];

  const openingHoursData =
    (openingHoursGroup?.tradingTimes &&
      mapLocationHours(openingHoursGroup.tradingTimes)) ??
    [];

  const nonTradingDatesColumns: TableColumnsType<NonTradingDatesModel> = [
    {
      title: t('common.date.date'),
      key: 'date',
      dataIndex: 'date',
      render: (value) => convertISOStringToLocalDate(value),
    },
  ];

  const locationGroupLoading = isLoadingGroup && openingGroupId;
  return (
    <PosPageContent>
      {!locationGroupLoading && !openingHoursGroup && (
        <Alert
          message={t('locations.trading.noOpeningHoursGroupAssignedError')}
          type="warning"
          showIcon
          style={{ marginBottom: token.marginMD }}
        />
      )}
      <PosCard
        headerProps={{
          title: t('locations.trading.hoursGroup'),
          icon: faClock,
        }}
      >
        <Space
          style={{
            marginBottom: canEditOpeningHours ? token.margin : 0,
            color: token.colorTextSecondary,
          }}
        >
          <Typography.Text strong>
            {t('locations.trading.activeGroup')}
          </Typography.Text>
          <Typography.Text>
            {!locationGroupLoading ? (
              openingHoursGroup ? (
                openingHoursGroup?.groupName
              ) : (
                t('locations.noAssignedGroup')
              )
            ) : (
              <Spin size="small" />
            )}
          </Typography.Text>
        </Space>
        {canEditOpeningHours && (
          <Form>
            <Row gutter={token.marginLG}>
              <Col span={24}>
                <Row>
                  <Form.Item style={{ minWidth: '25%', margin: 0 }}>
                    <InfiniteLoadingSelect
                      showSearch
                      loading={isLoadingAllGroups}
                      hasMoreResults={!!openingHoursGroups?.results.length}
                      placeholder={t('locations.trading.selectNewGroup')}
                      options={openingHoursGroups?.results ?? []}
                      labelKey="groupName"
                      onChange={(value) => {
                        setSelectedOpeningHoursGroupId(value);
                      }}
                      onLoadNextPage={() =>
                        onPageChange(formFilters.pageNumber + 1)
                      }
                      filterOption={(input, option) =>
                        (option?.children ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onSearch={() =>
                        handleFiltersChange([
                          { filter: 'pageNumber', value: 1 },
                        ])
                      }
                    />
                  </Form.Item>
                  <Button
                    style={{ marginLeft: token.marginSM }}
                    onClick={showModal}
                    type="primary"
                    disabled={
                      !selectedOpeningHoursGroupId ||
                      location.openingHoursGroupId ===
                        selectedOpeningHoursGroupId
                    }
                  >
                    {t('common.buttons.apply')}
                  </Button>
                </Row>
                <Row></Row>
              </Col>
            </Row>
          </Form>
        )}
      </PosCard>
      <PosCard
        headerProps={{
          title: t('locations.trading.openingHours'),
          icon: faBusinessTime,
        }}
        style={{
          marginTop: token.marginLG,
        }}
      >
        <PosTable
          id="location-hours-group-table"
          dataSource={openingHoursData}
          columns={openingHoursColumns}
          isLoading={isLoadingGroup && openingGroupId}
          size="middle"
        />
      </PosCard>
      <PosCard
        headerProps={{
          title: t('locations.trading.nonTradingDates'),
          icon: faBusinessTime,
        }}
        style={{
          marginTop: token.marginLG,
        }}
      >
        <PosTable
          id="non-trading-dates-table"
          dataSource={openingHoursGroup?.nonTradingDates ?? []}
          columns={nonTradingDatesColumns}
          isLoading={isLoadingGroup && openingGroupId}
          size="middle"
        />
      </PosCard>
      <LocationHoursModal
        openingHoursGroups={openingHoursGroups?.results}
        currentOpeningHoursGroup={openingHoursData}
        currentOpeningHoursGroupName={openingHoursGroup?.groupName}
        selectedOpeningHoursGroupId={selectedOpeningHoursGroupId}
        handleOpeningHoursGroupAssign={handleOpeningHoursGroupAssign}
        hideModal={hideModal}
        isModalOpen={isModalOpen}
        isUpdating={isUpdatingGroup}
      />
    </PosPageContent>
  );
};
